'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// lib
import { ThemeColor } from '@components/ui/_support'

/* -----------------------------------------------------------------------------
 * taxonomy
 *
 * The opportunity types `/dashboard/member_opportunities` sends, with their
 * labels and colours. Every colour resolves to a `pro*` theme token rather
 * than a raw hex so the identity stays in one place.
 * -------------------------------------------------------------------------- */

// the filter value that leaves a filter off
export const ALL_FILTER = 'All'

export type OpportunityTypeStyle = {
  bgColor: ThemeColor
  dotColor: ThemeColor
  label: string
  labelColor: ThemeColor
}

// the types the backend named on 2026-09-16. Others may come at any time and
// render through `getOpportunityTypeStyle`'s fallback
export const OPPORTUNITY_TYPES: Record<string, OpportunityTypeStyle> = {
  duplication_of_therapy: {
    bgColor: 'proDangerTint',
    dotColor: 'proDanger',
    label: 'Duplication of therapy',
    labelColor: 'proDangerDeep'
  },
  copay_card: {
    bgColor: 'proTealTint',
    dotColor: 'proTeal',
    label: 'Copay card',
    labelColor: 'proTealDeep'
  },
  new_to_therapy: {
    bgColor: 'proAquaTint',
    dotColor: 'proAqua',
    label: 'New to therapy',
    labelColor: 'proAquaDeep'
  },
  '30_to_90_days_supply': {
    bgColor: 'proNavyTint',
    dotColor: 'proNavy',
    label: '30 to 90 day supply',
    labelColor: 'proNavy'
  }
}

// written out rather than read off the object, whose key order is not
// something to rely on for tiles
export const OPPORTUNITY_TYPE_KEYS = [
  'duplication_of_therapy',
  'copay_card',
  'new_to_therapy',
  '30_to_90_days_supply'
]

// a type this screen does not know yet still renders, labelled from its key
export const getOpportunityTypeStyle = (
  type: string | null
): OpportunityTypeStyle =>
  OPPORTUNITY_TYPES[type] ?? {
    bgColor: 'proNeutralTint',
    dotColor: 'proTextMuted',
    label: type
      ? `${type.charAt(0).toUpperCase()}${type.slice(1).replace(/_/g, ' ')}`
      : 'Other',
    labelColor: 'proTextMuted'
  }

export type Priority = 'High' | 'Low' | 'Medium'

export const PRIORITY_KEYS: Priority[] = ['High', 'Medium', 'Low']

export type PriorityStyle = {
  bgColor: ThemeColor
  labelColor: ThemeColor
  rank: number
}

export const PRIORITIES: Record<Priority, PriorityStyle> = {
  High: { bgColor: 'proDangerTint', labelColor: 'proDangerDeep', rank: 0 },
  Low: { bgColor: 'proNeutralTint', labelColor: 'proTextMuted', rank: 2 },
  Medium: { bgColor: 'proAccentTint', labelColor: 'proAccentDeep', rank: 1 }
}
